import { create } from 'zustand'
import type { User } from '@/types'

interface ImpersonationInfo {
  id: string
  email: string
}

/**
 * Defense in depth: /users/me should carry tenantId, but if it's ever missing
 * (as it was before the users-me-tenant-id fix), fall back to the tenantId
 * embedded in the JWT so tenant-scoped UI — e.g. the dashboard onboarding gate,
 * `if (!user?.tenantId) return` — doesn't silently render nothing. Also
 * auto-heals already-cached tenantId-less users on the next hydrate.
 */
function withTenantId(user: User, token: string | null): User {
  if (user.tenantId || !token) return user
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload?.tenantId) return { ...user, tenantId: payload.tenantId }
  } catch {
    // malformed token — leave the user object untouched
  }
  return user
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isImpersonating: boolean
  impersonatedBy: ImpersonationInfo | null

  login: (user: User, token: string) => void
  logout: () => void
  setUser: (user: User) => void
  setLoading: (loading: boolean) => void
  hydrate: () => void
  startImpersonation: (user: User, token: string, impersonatedBy: ImpersonationInfo) => void
  endImpersonation: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isImpersonating: false,
  impersonatedBy: null,

  login: (user, token) => {
    const safeUser = withTenantId(user, token)
    localStorage.setItem('paver_token', token)
    localStorage.setItem('paver_user', JSON.stringify(safeUser))
    set({ user: safeUser, token, isAuthenticated: true, isLoading: false })
  },

  logout: () => {
    localStorage.removeItem('paver_token')
    localStorage.removeItem('paver_user')
    localStorage.removeItem('paver_impersonation')
    // Onboarding dismiss/intro flags are per-tenant caches, not user prefs —
    // left behind they override the server for the NEXT login (edge case #27).
    // Standard length/key(i) API: Object.keys(localStorage) is unreliable on stubs.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && (k.startsWith('paver_onboarding_dismissed_') || k.startsWith('paver_setup_guide_intro_'))) {
        localStorage.removeItem(k)
      }
    }
    set({ user: null, token: null, isAuthenticated: false, isLoading: false, isImpersonating: false, impersonatedBy: null })
  },

  setUser: (user) => {
    localStorage.setItem('paver_user', JSON.stringify(user))
    set({ user })
  },

  setLoading: (isLoading) => set({ isLoading }),

  hydrate: () => {
    try {
      const token = localStorage.getItem('paver_token')
      const userJson = localStorage.getItem('paver_user')
      const impersonationJson = localStorage.getItem('paver_impersonation')

      if (token && userJson) {
        const user = withTenantId(JSON.parse(userJson) as User, token)
        const impersonation = impersonationJson ? JSON.parse(impersonationJson) as ImpersonationInfo : null
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          isImpersonating: !!impersonation,
          impersonatedBy: impersonation,
        })
      } else {
        set({ isLoading: false })
      }
    } catch {
      localStorage.removeItem('paver_token')
      localStorage.removeItem('paver_user')
      localStorage.removeItem('paver_impersonation')
      set({ isLoading: false })
    }
  },

  startImpersonation: (user, token, impersonatedBy) => {
    const safeUser = withTenantId(user, token)
    localStorage.setItem('paver_token', token)
    localStorage.setItem('paver_user', JSON.stringify(safeUser))
    localStorage.setItem('paver_impersonation', JSON.stringify(impersonatedBy))
    set({
      user: safeUser,
      token,
      isAuthenticated: true,
      isLoading: false,
      isImpersonating: true,
      impersonatedBy,
    })
  },

  endImpersonation: () => {
    localStorage.removeItem('paver_token')
    localStorage.removeItem('paver_user')
    localStorage.removeItem('paver_impersonation')
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isImpersonating: false,
      impersonatedBy: null,
    })
  },
}))
